import { ApolloProvider } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useParams,
} from 'react-router-dom';
import 'supertokens-auth-react';
import * as Session from 'supertokens-auth-react/recipe/session';
import {
  SessionAuth,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';

import { apolloClientFactory, defaultCache } from '@eluve/apollo-client';
import { TenantToOrganizationRedirect } from '@eluve/app-router-utils';
import {
  BrowserCompatibilityGate,
  NotFoundBoundary,
  NotFoundPage,
} from '@eluve/blocks';
import { LoadingIndicator, Main } from '@eluve/components';
import {
  CareLibraryDetailPage,
  CareLibraryPage,
} from '@eluve/feature-care-library';
import {
  CarePlanDetailPage,
  CarePlansListPage,
  PlanBuilderPage,
} from '@eluve/feature-care-plans';
import {
  ErrorFallbackRender,
  LoginCallbackPage,
  LogoutPage,
  MagicLinkVerifyPage,
  SsoPage,
} from '@eluve/feature-login';
import {
  CareTeam,
  MedicalProfile,
  Observations,
  OverviewCardGroup,
  PatientArtifacts,
  PatientCarePlanDetail,
  PatientCarePlans,
  PatientDetailsImportedNotes,
  PatientDocumentsTab,
  PatientFiles,
  PatientOverviewTab,
  PatientProfileAppointmentsRoute,
  PatientProfileGate,
  PatientProfileIndexRedirect,
  PatientProfileRouteGuard,
  PatientsListPage,
} from '@eluve/feature-patients';
import { PatientCarePlansSection } from '@eluve/fhir-care-plans';
import { PatientConditionsSection } from '@eluve/fhir-conditions';
import { PatientGoalsSection } from '@eluve/fhir-goals';
import { PatientObservationsSection } from '@eluve/fhir-observations';
import { PatientTodosSection } from '@eluve/fhir-service-requests';
import { ServiceDetailPage, ServicesCatalogPage } from '@eluve/fhir-services';
import { MyTasksSection } from '@eluve/fhir-tasks';
import { createAdminRoutes } from '@eluve/frontend-admin';
import {
  AppointmentDetailsOutput,
  AppointmentDetailsPage,
  AppointmentPdfPage,
} from '@eluve/frontend-feature-appointment';
import { EHRSyncStoreProvider } from '@eluve/frontend-feature-ehr-sync';
import {
  EditTemplateBundlePage,
  GenerateWithAiPage,
  NewTemplateBundlePage,
  TemplatesGuard,
  TenantTemplatesIndexPage,
  outputTemplateRoutes,
} from '@eluve/frontend-feature-output-templates';
import { PromptsPage } from '@eluve/frontend-feature-prompt-templates';
import { PromptsGuard } from '@eluve/frontend-feature-prompt-templates';
import {
  LocationPage,
  LocationsPage,
  RegenerateNotesPage,
  SessionTypesPage,
  UserGroupDetailsPage,
  UserGroupsListPage,
  UsersPage,
} from '@eluve/frontend-feature-tenants';
import { createReviewRoutes } from '@eluve/frontend-review-dash';
import {
  MaintenanceGate,
  SessionlessAnalyticsProvider,
  UrlParamNormalizer,
  getPostHogHostConfig,
} from '@eluve/posthog-react';
import { SessionContextProvider } from '@eluve/session-helpers';
import { SiteBannerProvider } from '@eluve/site-banners';
import {
  ChatAccessGate,
  FeatureFlaggedPage,
  PostHogFeatureFlaggedPage,
  TenantOrUserFeatureFlaggedComponent,
} from '@eluve/smart-blocks';
import {
  AppointmentFilesList,
  AppointmentRecordingStatusStoreProvider,
} from '@eluve/user-local-files';

import {
  AnonymousIntercomProvider,
  IntercomProvider,
} from './IntercomProvider';
import { SessionBootstrappedAnalyticsProvider } from './SessionBootstrappedAnalyticsProvider';
import { appConfig } from './config';
import { ImpersonationLoginPage } from './routes/ImpersonationLoginPage';
import { LoginPageWrapper } from './routes/LoginPageWrapper';
import { WaitListPage } from './routes/WaitlistPage';
import { AppIndexPage } from './routes/app/AppIndexPage';
import { AppLayout } from './routes/app/AppLayout';
import { CompatibilityChecker } from './routes/app/CompatibilityChecker';
import { RemoveSegmentRedirect } from './routes/app/RemoveSegmentRedirect';
import { SingularEntityRedirect } from './routes/app/SingularEntityRedirect';
import { UserContextProvider } from './routes/app/UserContextProvider';
import { VersionChecker } from './routes/app/VersionChecker';
import { createEvalRoutes } from './routes/app/eval/eval-routes';
import { createPatientRoutes } from './routes/app/patient/patient-routes';
import { SettingsPage } from './routes/app/settings/SettingsPage';
import { HomePage } from './routes/app/tenant/$tenantId/HomePage';
import { InterstitialsWrapper } from './routes/app/tenant/$tenantId/InterstitialsWrapper';
import { PeriodicFileSyncProviderBridge } from './routes/app/tenant/$tenantId/PeriodicFileSyncProviderBridge';
import { TaskProcessorProviderBridge } from './routes/app/tenant/$tenantId/TaskProcessorProviderBridge';
import { TenantAdminGuard } from './routes/app/tenant/$tenantId/TenantAdminGuard';
import { TenantGuard } from './routes/app/tenant/$tenantId/TenantGuard';
import { TenantLayout } from './routes/app/tenant/$tenantId/TenantLayout';
import { TenantProvider } from './routes/app/tenant/$tenantId/TenantProvider';
import { AppointmentPage } from './routes/app/tenant/$tenantId/appointments/Appointment.Page';
import { AppointmentDialogProvider } from './routes/app/tenant/$tenantId/appointments/AppointmentDialogProvider';
import { AppointmentsListPage } from './routes/app/tenant/$tenantId/appointments/AppointmentsListPage';
import { AppointmentsMainLayout } from './routes/app/tenant/$tenantId/appointments/AppointmentsMainLayout';
import { SummaryComparisonPage } from './routes/app/tenant/$tenantId/appointments/SummaryComparison.Page';
import { TranscriptionMachineProvider } from './routes/app/tenant/$tenantId/appointments/machines/transcription/TranscriptionMachineProvider';
import { ReportingDashboardPage } from './routes/app/tenant/$tenantId/reporting/ReportingDashboardPage';
import { SettingsShell } from './routes/app/tenant/$tenantId/settings/SettingsShell';
import { TenantUserInsightsSettingsPage } from './routes/app/tenant/$tenantId/settings/insights/TenantUserInsightsSettingsPage';
import { AppGrantsSettingsPage } from './routes/app/tenant/$tenantId/settings/pages/AppGrantsSettingsPage';
import { AudioDisplaySettingsPage } from './routes/app/tenant/$tenantId/settings/pages/AudioDisplaySettingsPage';
import { NoteSettingsPage } from './routes/app/tenant/$tenantId/settings/pages/NoteSettingsPage';
import { OrganizationGeneralPage } from './routes/app/tenant/$tenantId/settings/pages/OrganizationGeneralPage';
import { OrganizationLayersPage } from './routes/app/tenant/$tenantId/settings/pages/OrganizationLayersPage';
import { ShortcutsSettingsPage } from './routes/app/tenant/$tenantId/settings/pages/ShortcutsSettingsPage';
import { UserSettingsPage } from './routes/app/tenant/$tenantId/settings/pages/UserSettingsPage';
import { ChatConversationStartersPage } from './routes/app/tenant/$tenantId/settings/pages/chat/ChatConversationStartersPage';
import { ChatCustomInstructionsPage } from './routes/app/tenant/$tenantId/settings/pages/chat/ChatCustomInstructionsPage';
import { ChatOutputPreferencesPage } from './routes/app/tenant/$tenantId/settings/pages/chat/ChatOutputPreferencesPage';
import { ChatSettingsPage } from './routes/app/tenant/$tenantId/settings/pages/chat/ChatSettingsPage';
import {
  AdminToSettingsRedirect,
  OutputTemplatesToSettingsRedirect,
  TenantSettingsRedirect,
} from './routes/app/tenant/$tenantId/settings/settingsRedirects';
import { TasksListPage } from './routes/app/tenant/$tenantId/tasks/TasksListPage';
import { CarePlanFeatureFlaggedPage } from './routes/app/tenant/CarePlanFeatureGate';
import { PendingApprovalPage } from './routes/app/tenant/PendingApprovalPage';
import { TenantsPage } from './routes/app/tenant/TenantsPage';
import { sessionActor, tokenProvider } from './session-token-provider';

const rootApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const adminApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  desiredRole: 'eluve-admin',
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const reviewerApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  /**
   * Review dashboard is shared between tenant reviewers and ML engineers.
   * The `ml-engineer` Hasura role is a superset of `reviewer` (sees tenants
   * via either membership), so prefer it when the user is provisioned as
   * both — that way dual-role users see the union of their access.
   */
  desiredRole: async () => {
    try {
      const payload = (await Session.getAccessTokenPayloadSecurely()) as {
        isReviewer?: boolean;
        isMlEngineer?: boolean;
      };
      if (payload?.isMlEngineer) return 'ml-engineer';
      if (payload?.isReviewer) return 'reviewer';
    } catch {
      // fall through to session default role
    }
    return undefined;
  },
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const tenantScopedApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const postHogHost = getPostHogHostConfig(
  appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
  appConfig.VITE_POSTHOG_PROXY_HOST,
);

const PatientNotFoundBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { patientId } = useParams();
  return (
    <NotFoundBoundary
      entityType="Patient"
      listPathTemplate="/organizations/:tenantId/patients"
      listLabel="Back to patients"
      resetKeys={[patientId]}
    >
      {children}
    </NotFoundBoundary>
  );
};

const AppointmentNotFoundBoundary: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { appointmentId } = useParams();
  return (
    <NotFoundBoundary
      entityType="Appointment"
      listPathTemplate="/organizations/:tenantId/appointments"
      listLabel="Back to appointments"
      resetKeys={[appointmentId]}
    >
      {children}
    </NotFoundBoundary>
  );
};

const routes = [
  {
    path: '/login',
    element: (
      <UrlParamNormalizer>
        <SessionlessAnalyticsProvider
          apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
          apiHost={postHogHost}
        >
          <MaintenanceGate>
            <AnonymousIntercomProvider>
              <LoginPageWrapper />
            </AnonymousIntercomProvider>
          </MaintenanceGate>
        </SessionlessAnalyticsProvider>
      </UrlParamNormalizer>
    ),
  },
  {
    path: '/sign-out',
    element: (
      <SessionlessAnalyticsProvider
        apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
        apiHost={postHogHost}
      >
        <LogoutPage medplumBaseUrl={appConfig.VITE_MEDPLUM_BASE_URL} />
      </SessionlessAnalyticsProvider>
    ),
  },
  {
    path: '/login/callback/*',
    element: <LoginCallbackPage />,
  },
  {
    path: '/impersonate/callback',
    element: (
      <SessionAuth>
        <ImpersonationLoginPage />
      </SessionAuth>
    ),
  },
  {
    path: '/sso',
    element: <SsoPage />,
  },
  {
    path: '/waitlist',
    element: <WaitListPage />,
  },
  {
    path: '/auth/verify',
    element: <MagicLinkVerifyPage />,
  },
  {
    path: '/',
    element: (
      <ErrorBoundary
        showDialog={true}
        fallback={(props) => (
          <ErrorFallbackRender {...props} surface="web-vite" />
        )}
      >
        <SessionAuth>
          <SessionContextProvider useSessionContext={useSessionContext}>
            <SessionBootstrappedAnalyticsProvider apiHost={postHogHost}>
              <MaintenanceGate>
                <UserContextProvider>
                  <CompatibilityChecker>
                    <VersionChecker intervalMinutes={5}>
                      <ApolloProvider client={rootApolloClient}>
                        <IntercomProvider>
                          <SiteBannerProvider>
                            <AppLayout />
                          </SiteBannerProvider>
                        </IntercomProvider>
                      </ApolloProvider>
                    </VersionChecker>
                  </CompatibilityChecker>
                </UserContextProvider>
              </MaintenanceGate>
            </SessionBootstrappedAnalyticsProvider>
          </SessionContextProvider>
        </SessionAuth>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <AppIndexPage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'tenant/*',
        element: <TenantToOrganizationRedirect />,
      },
      {
        path: 'tenants/*',
        element: <TenantToOrganizationRedirect />,
      },
      {
        path: 'organization/*',
        element: <SingularEntityRedirect entityName="organization" />,
      },
      {
        path: 'organizations/switch/*',
        element: <RemoveSegmentRedirect segment="switch" />,
      },
      {
        path: 'organizations',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <TenantsPage />,
          },
          {
            path: 'pending-approval',
            element: <PendingApprovalPage />,
          },
          {
            path: ':tenantId',
            element: (
              <TenantGuard
                client={tenantScopedApolloClient}
                sessionActor={sessionActor}
              >
                <Suspense fallback={<LoadingIndicator size="xl" />}>
                  <EHRSyncStoreProvider
                    initializer={(set) => ({
                      lastSuccessImportCompletedAt: undefined,
                      isDataImporting: undefined,
                      setIsDataImporting: (isLoading) =>
                        set({ isDataImporting: isLoading }),
                    })}
                  >
                    <TenantProvider>
                      <InterstitialsWrapper>
                        <AppointmentRecordingStatusStoreProvider
                          initializer={() => ({
                            isActivelyRecording: false,
                          })}
                        >
                          <TaskProcessorProviderBridge>
                            <PeriodicFileSyncProviderBridge>
                              <TranscriptionMachineProvider>
                                <AppointmentDialogProvider>
                                  <Outlet />
                                </AppointmentDialogProvider>
                              </TranscriptionMachineProvider>
                            </PeriodicFileSyncProviderBridge>
                          </TaskProcessorProviderBridge>
                        </AppointmentRecordingStatusStoreProvider>
                      </InterstitialsWrapper>
                    </TenantProvider>
                  </EHRSyncStoreProvider>
                </Suspense>
              </TenantGuard>
            ),
            children: [
              {
                path: 'reporting',
                element: (
                  <>
                    <Helmet>
                      <title>Reports | Eluve</title>
                    </Helmet>
                    <ReportingDashboardPage />
                  </>
                ),
              },
              {
                path: 'settings',
                element: <SettingsShell />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="user" replace />,
                  },
                  {
                    path: 'user',
                    element: <UserSettingsPage />,
                  },
                  {
                    path: 'audio',
                    element: <AudioDisplaySettingsPage />,
                  },
                  {
                    path: 'shortcuts',
                    element: <ShortcutsSettingsPage />,
                  },
                  {
                    path: 'app-grants',
                    element: <AppGrantsSettingsPage />,
                  },
                  {
                    path: 'note',
                    element: <NoteSettingsPage />,
                  },
                  {
                    path: 'chat',
                    element: (
                      <ChatAccessGate>
                        <Outlet />
                      </ChatAccessGate>
                    ),
                    children: [
                      {
                        index: true,
                        element: <ChatSettingsPage />,
                      },
                      {
                        path: 'output-preferences',
                        element: <ChatOutputPreferencesPage />,
                      },
                      {
                        path: 'custom-instructions',
                        element: <ChatCustomInstructionsPage />,
                      },
                      {
                        path: 'conversation-starters',
                        element: <ChatConversationStartersPage />,
                      },
                    ],
                  },
                  {
                    path: 'insights',
                    element: (
                      <PostHogFeatureFlaggedPage flag="EXTENSION_PREVISIT_INSIGHTS">
                        <Main>
                          <TenantUserInsightsSettingsPage />
                        </Main>
                      </PostHogFeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'templates',
                    element: (
                      <TemplatesGuard>
                        <Helmet>
                          <title>Templates | Eluve</title>
                        </Helmet>
                        <Outlet />
                      </TemplatesGuard>
                    ),
                    children: [
                      {
                        index: true,
                        element: <TenantTemplatesIndexPage />,
                      },
                      {
                        element: (
                          <Main>
                            <Outlet />
                          </Main>
                        ),
                        children: [
                          {
                            path: 'new',
                            element: <NewTemplateBundlePage />,
                          },
                          {
                            path: 'new/ai',
                            element: <GenerateWithAiPage />,
                          },
                          {
                            path: 'bundles/:outputTemplateId',
                            element: <EditTemplateBundlePage />,
                          },
                          ...outputTemplateRoutes,
                        ],
                      },
                    ],
                  },
                  {
                    element: (
                      <TenantAdminGuard>
                        <Outlet />
                      </TenantAdminGuard>
                    ),
                    children: [
                      {
                        path: 'organization',
                        element: <OrganizationGeneralPage />,
                      },
                      {
                        path: 'organization/layers',
                        element: (
                          <FeatureFlaggedPage flag="LOCATIONS">
                            <OrganizationLayersPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'members',
                        element: <UsersPage />,
                      },
                      {
                        path: 'session-types',
                        element: (
                          <FeatureFlaggedPage flag="SESSION_TYPES">
                            <SessionTypesPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'locations',
                        element: (
                          <FeatureFlaggedPage flag="LOCATIONS">
                            <Outlet />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <LocationsPage />,
                          },
                          {
                            path: ':locationId',
                            element: <LocationPage />,
                          },
                        ],
                      },
                      {
                        path: 'user-groups',
                        element: (
                          <FeatureFlaggedPage flag="USER_GROUPS">
                            <Outlet />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <UserGroupsListPage />,
                          },
                          {
                            path: ':userGroupId',
                            element: <UserGroupDetailsPage />,
                          },
                        ],
                      },
                      {
                        path: 'regenerate-notes',
                        element: (
                          <FeatureFlaggedPage flag="ENABLE_TENANT_ADMIN_REGENERATE_NOTE">
                            <RegenerateNotesPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'care-plans',
                        element: (
                          <PostHogFeatureFlaggedPage flag="CARE_PLANS">
                            <FeatureFlaggedPage flag="MEDPLUM">
                              <Outlet />
                            </FeatureFlaggedPage>
                          </PostHogFeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <CarePlansListPage />,
                          },
                          {
                            path: 'new',
                            element: (
                              <PlanBuilderPage
                                baseUrl={appConfig.VITE_API_DOMAIN}
                              />
                            ),
                          },
                          {
                            path: ':planId',
                            element: <CarePlanDetailPage />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'chat-settings',
                element: <TenantSettingsRedirect to="chat" />,
              },
              {
                path: 'insights-settings',
                element: <TenantSettingsRedirect to="insights" />,
              },
              {
                path: 'admin/*',
                element: <AdminToSettingsRedirect />,
              },
              {
                path: 'output-templates/*',
                element: <OutputTemplatesToSettingsRedirect />,
              },
              {
                element: <TenantLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="home" replace />,
                  },
                  {
                    path: 'home',
                    element: <HomePage />,
                  },
                  {
                    path: 'files',
                    element: (
                      <FeatureFlaggedPage flag="LOCAL_FILES_MANAGEMENT">
                        <Helmet>
                          <title>Files | Eluve</title>
                        </Helmet>
                        <AppointmentFilesList />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'patient/*',
                    element: <SingularEntityRedirect entityName="patient" />,
                  },
                  {
                    path: 'appointments',
                    element: (
                      <FeatureFlaggedPage flag="APPOINTMENTS_VIEW">
                        <Helmet>
                          <title>Appointments | Eluve</title>
                        </Helmet>
                        <AppointmentsListPage />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'patients',
                    element: (
                      <FeatureFlaggedPage flag="PATIENTS_VIEW">
                        <PatientsListPage />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    // Org-wide service catalog (HealthcareService CRUD).
                    path: 'services',
                    element: (
                      <FeatureFlaggedPage flag="MEDPLUM">
                        <Helmet>
                          <title>Services | Eluve</title>
                        </Helmet>
                        <Outlet />
                      </FeatureFlaggedPage>
                    ),
                    children: [
                      {
                        index: true,
                        element: <ServicesCatalogPage />,
                      },
                      {
                        path: ':serviceId',
                        element: <ServiceDetailPage />,
                      },
                    ],
                  },
                  {
                    path: 'tasks',
                    element: (
                      // Double gate matching the Clinical Tasks panel: tenant
                      // flag AND PostHog user flag.
                      <FeatureFlaggedPage flag="CLINICAL_TASKS">
                        <PostHogFeatureFlaggedPage flag="CLINICAL_TASKS">
                          <Helmet>
                            <title>Tasks | Eluve</title>
                          </Helmet>
                          <TasksListPage />
                        </PostHogFeatureFlaggedPage>
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'patients/:patientId',
                    element: (
                      <FeatureFlaggedPage flag="PATIENTS_VIEW">
                        <PatientNotFoundBoundary>
                          <PatientProfileGate
                            baseUrl={appConfig.VITE_API_DOMAIN}
                          />
                        </PatientNotFoundBoundary>
                      </FeatureFlaggedPage>
                    ),
                    children: [
                      {
                        index: true,
                        element: <PatientProfileIndexRedirect />,
                      },
                      {
                        path: 'overview',
                        element: (
                          <PatientProfileRouteGuard owner="profile">
                            <PatientOverviewTab>
                              <PatientCarePlansSection />
                              <OverviewCardGroup title="Clinical record">
                                <PatientConditionsSection />
                                <PatientGoalsSection />
                                <PatientObservationsSection />
                              </OverviewCardGroup>
                              <OverviewCardGroup title="Action items">
                                <MyTasksSection />
                                <PatientTodosSection />
                              </OverviewCardGroup>
                            </PatientOverviewTab>
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'documents',
                        element: (
                          <PatientProfileRouteGuard owner="profile">
                            <PatientDocumentsTab />
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'artifacts',
                        element: (
                          <PatientProfileRouteGuard
                            owner="legacy"
                            redirectTo="../documents"
                          >
                            <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
                              <PatientArtifacts />
                            </TenantOrUserFeatureFlaggedComponent>
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'appointments',
                        element: <PatientProfileAppointmentsRoute />,
                      },
                      {
                        path: 'imported-notes',
                        element: (
                          <PatientProfileRouteGuard
                            owner="legacy"
                            redirectTo="../documents"
                          >
                            <PatientDetailsImportedNotes />
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'medical-profile',
                        element: (
                          <PatientProfileRouteGuard owner="legacy">
                            <MedicalProfile />
                          </PatientProfileRouteGuard>
                        ),
                        children: [
                          {
                            index: true,
                            element: <Navigate to="care-team" replace />,
                          },
                          {
                            path: 'care-team',
                            element: <CareTeam />,
                          },
                          {
                            path: 'observations',
                            element: <Observations />,
                          },
                          {
                            path: 'patient-uploads',
                            element: <PatientFiles />,
                          },
                        ],
                      },
                      {
                        path: 'care-plans',
                        element: (
                          <PatientProfileRouteGuard owner="legacy">
                            <PatientCarePlans />
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'care-plans/:carePlanId',
                        element: (
                          <PatientProfileRouteGuard owner="legacy">
                            <PatientCarePlanDetail />
                          </PatientProfileRouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'care-library',
                    element: (
                      <CarePlanFeatureFlaggedPage>
                        <Helmet>
                          <title>Care Library | Eluve</title>
                        </Helmet>
                        <CareLibraryPage />
                      </CarePlanFeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'care-library/:offeringId',
                    element: (
                      <CarePlanFeatureFlaggedPage>
                        <CareLibraryDetailPage />
                      </CarePlanFeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'appointment/*',
                    element: (
                      <SingularEntityRedirect entityName="appointment" />
                    ),
                  },
                  {
                    path: 'appointments/:appointmentId',
                    element: (
                      <AppointmentNotFoundBoundary>
                        <AppointmentsMainLayout />
                      </AppointmentNotFoundBoundary>
                    ),
                    children: [
                      {
                        index: true,
                        element: <AppointmentPage />,
                      },
                      {
                        path: 'comparison',
                        element: <SummaryComparisonPage />,
                      },
                      {
                        path: 'pdf',
                        element: (
                          <FeatureFlaggedPage flag="PDF_OUTPUT">
                            <AppointmentPdfPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'details',
                        element: (
                          <FeatureFlaggedPage flag="LLM_OUTPUT_DETAILS">
                            <AppointmentDetailsPage />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <div className="text-text-tertiary">
                                Select Output types
                              </div>
                            ),
                          },
                          {
                            path: ':outputType',
                            element: <AppointmentDetailsOutput />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'prompts',
        element: (
          <PromptsGuard>
            <ApolloProvider client={adminApolloClient}>
              <PromptsPage />
            </ApolloProvider>
          </PromptsGuard>
        ),
      },
      ...createAdminRoutes({
        path: 'admin',
        client: adminApolloClient,
        tenantScopedApolloClient,
        sessionActor,
        postHogProjectId: appConfig.VITE_POSTHOG_PROJECT_ID,
        langfuseBaseUrl: appConfig.VITE_LANGFUSE_BASE_URL,
        langfuseProjectId: appConfig.VITE_LANGFUSE_PROJECT_ID,
      }),
      ...createEvalRoutes('eval'),
      ...createReviewRoutes('review', reviewerApolloClient),
    ],
  },
  ...createPatientRoutes('/patient-organization', rootApolloClient),
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export const reactRouter = createBrowserRouter([
  {
    element: <BrowserCompatibilityGate />,
    children: routes,
  },
]);
